import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useTranslation } from 'react-i18next';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock
} from 'lucide-react';
import { WhatsAppIcon } from '../components/WhatsAppIcon';

const openWhatsApp = () => {
  window.open('https://wa.me/917702099393?text=' + encodeURIComponent('Hello Stigmaflora! I would like to know more about your products.'), '_blank', 'noopener,noreferrer');
};

export const ContactPage = () => {
  const { t } = useTranslation();
  return (
    <div data-testid="contact-page" className="min-h-screen pt-20 bg-slate-50">
      {/* Header */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-emerald-900 to-emerald-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center">
            <span className="text-sm uppercase tracking-widest font-semibold text-emerald-400 mb-4 block">
              {t('contact.span')}
            </span>
            <h1 
              data-testid="contact-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] tracking-tight mb-6"
            >
              {t('contact.title')}
            </h1>
            <p className="text-lg text-emerald-200 max-w-2xl mx-auto">
              {t('contact.desc')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* WhatsApp CTA - Prominent */}
          <div className="mb-16">
            <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 overflow-hidden shadow-lg">
              <CardContent className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <WhatsAppIcon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-slate-900 font-['Outfit']">
                        {t('contact.wa_title')}
                      </h3>
                      <p className="text-slate-600 text-lg">
                        {t('contact.wa_desc')}
                      </p>
                    </div>
                  </div>
                  <Button
                    data-testid="whatsapp-chat-btn"
                    onClick={openWhatsApp}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg cursor-pointer whitespace-nowrap"
                  >
                    <WhatsAppIcon className="w-5 h-5 mr-2" />
                    {t('contact.wa_btn')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Phone Card */}
            <Card className="border-emerald-100 overflow-hidden shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-700 mb-6">
                  <Phone className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-3">
                  {t('contact.cc_title')}
                </h3>
                <p className="text-slate-600 text-base mb-6">
                  {t('contact.cc_desc')}
                </p>
                <a
                  href="tel:7702099393"
                  data-testid="contact-phone"
                  className="text-emerald-700 font-bold text-2xl hover:text-emerald-900 transition-colors"
                >
                  +91 7702099393
                </a>
                <p className="text-slate-500 text-sm mt-4">
                  {t('contact.cc_time')}
                </p>
              </CardContent>
            </Card>

            {/* Email Card */}
            <Card className="border-amber-100 overflow-hidden shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center text-amber-700 mb-6">
                  <Mail className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-3">
                  {t('contact.email_title')}
                </h3>
                <p className="text-slate-600 text-base mb-6">
                  {t('contact.email_desc')}
                </p>
                <a
                  href="mailto:customercare@stigmaflora.com"
                  data-testid="contact-email"
                  className="text-amber-700 font-bold hover:text-amber-900 transition-colors break-all text-lg"
                >
                  customercare@stigmaflora.com
                </a>
              </CardContent>
            </Card>

            {/* Address Card */}
            <a
              href="https://www.google.com/maps/search/HMVR%2BJ63+Thumkunta+Telangana+India"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="border-teal-100 overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer md:col-span-2 lg:col-span-1 hover:scale-105">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center text-teal-700 mb-6">
                    <MapPin className="w-7 h-7" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-3">
                    {t('contact.visit_title')}
                  </h3>
                  <p className="text-teal-700 font-semibold mb-2 text-sm">{t('contact.visit_desc')}</p>
                  <p className="text-slate-600 text-base leading-relaxed hover:text-teal-700 transition-colors">
                    {t('contact.address_1')}<br />
                    {t('contact.address_2')}<br />
                    {t('contact.address_3')}<br />
                    {t('contact.address_4')}<br />
                    {t('contact.address_5')}
                  </p>
                </CardContent>
              </Card>
            </a>

            {/* Hours Card */}
            <Card className="border-slate-200 bg-emerald-50 overflow-hidden shadow-md hover:shadow-lg transition-shadow md:col-span-2 lg:col-span-1">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-emerald-200 rounded-xl flex items-center justify-center text-emerald-700 mb-6">
                  <Clock className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-3">
                  {t('contact.hours_title')}
                </h3>
                <div className="space-y-2 text-slate-700 text-base font-medium">
                  <p>{t('contact.hours_1')}</p>
                  <p>{t('contact.hours_2')}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

    </div>
  );
};

